/**
 * Product Hero Gallery instance lifecycle.
 *
 * Owns initialization, teardown, page restoration, and shared transient UI
 * cleanup without bootstrapping the public gallery runtime.
 */

(() => {
  const MODULE_KEY = '__MarineMaxProductHeroGalleryLifecycle';

  if (window[MODULE_KEY]?.install) return;

  class ProductHeroGalleryLifecycleBehavior {
    init() {
      this.heroMedia = this.heroBanner.querySelector('.product__hero-media');
      this.mediaActionButtons = this.heroBanner.querySelectorAll('[data-media-open]');
      this.mobileMediaHubBtn = this.heroBanner.querySelector(
        '[data-mobile-media-hub-open]',
      );

      this.parseImagesData();
      this.initNavigation();
      this.initTouchNavigation();
      this.initGalleryModal();
      this.initVideoModal();
      this.initTourModal();
      this.initMediaHubModal();
      this.initLightbox();
      this.initScrollIndicator();
      this.initHistorySync();
      this.syncCurrentIndexFromDom();
      this.ensureVisibleHeroState();
      this.preloadHeroNeighbors(this.currentIndex);
      this.images.forEach((slide) => this.setHeroBackgroundFocal(slide));
    }

    destroy() {
      if (this.isDestroyed) return;
      this.isDestroyed = true;

      this.destroyHeroCarousel();
      this.invalidateLightboxPendingRender();
      this.closeTransientUi({ restoreBodyScroll: true });
      if (this.hasManagedHistoryState()) {
        this.clearHistoryState({ replace: true });
      }

      this.eventAbortController?.abort();
      this.clearLightboxGestureTimer();

      if (this.pendingHistoryBackFallback) {
        window.clearTimeout(this.pendingHistoryBackFallback);
        this.pendingHistoryBackFallback = null;
      }

      this.destroyMediaHub();
      this.destroyLightboxCore();
      this.destroyLightboxGestures();

      if (this.heroBanner?.__productHeroGalleryInstance === this) {
        delete this.heroBanner.__productHeroGalleryInstance;
      }

      if (this.instanceRegistry?.get(this.sectionId) === this) {
        this.instanceRegistry.delete(this.sectionId);
      }
    }

    closeTransientUi({ restoreBodyScroll = true } = {}) {
      if (this.galleryModal) {
        this.galleryModal.classList.remove('is-open');
        this.galleryModal.setAttribute('aria-hidden', 'true');
      }
      if (this.videoModal) {
        this.stopVideoModalPlayback();
        this.videoModal.classList.remove('is-open');
        this.videoModal.setAttribute('aria-hidden', 'true');
      }
      if (this.tourModal) {
        this.stopTourModalPlayback();
        this.tourModal.classList.remove('is-open');
        this.tourModal.setAttribute('aria-hidden', 'true');
      }
      if (this.mediaHubModal) {
        this.mediaHubModal.classList.remove('is-open');
        this.mediaHubModal.setAttribute('aria-hidden', 'true');
      }
      if (this.lightbox) {
        this.stopLightboxVideoPlayback();
        this.resetLightboxInteractionState?.();
        this.resetLightboxZoom?.();
        this.lightbox.classList.remove('is-open');
        this.lightbox.setAttribute('aria-hidden', 'true');
      }

      if (!restoreBodyScroll) return;

      const hasOpenHeroUi =
        document.querySelector('.product__gallery-modal.is-open') ||
        document.querySelector('.product__lightbox.is-open') ||
        document.querySelector('.product__content-modal.is-open') ||
        document.querySelector('.product__media-hub-modal.is-open');
      if (!hasOpenHeroUi) {
        document.body.style.overflow = '';
      }
    }

    hasOpenTransientUi() {
      return !!(
        this.galleryModal?.classList.contains('is-open') ||
        this.videoModal?.classList.contains('is-open') ||
        this.tourModal?.classList.contains('is-open') ||
        this.mediaHubModal?.classList.contains('is-open') ||
        this.lightbox?.classList.contains('is-open')
      );
    }

    refreshAfterPageRestore() {
      this.parseImagesData();
      this.syncCurrentIndexFromDom();
      this.ensureVisibleHeroState();
      this.images.forEach((slide) => this.setHeroBackgroundFocal(slide));
      if (this.galleryModal?.classList.contains('is-open')) {
        this.renderGalleryImages();
      }
      this.syncUiToHistoryState();
    }

    initScrollIndicator() {
      this.scrollIndicatorBtn = this.heroBanner.querySelector('[data-hero-scroll]');
      if (!this.scrollIndicatorBtn) return;

      this.scrollIndicatorBtn.addEventListener(
        'click',
        () => {
          const top =
            window.scrollY +
            this.heroBanner.getBoundingClientRect().top +
            this.heroBanner.offsetHeight;

          window.scrollTo({
            top,
            behavior: 'smooth',
          });
        },
        { signal: this.eventAbortController.signal },
      );
    }
  }

  const descriptors = Object.getOwnPropertyDescriptors(
    ProductHeroGalleryLifecycleBehavior.prototype,
  );
  delete descriptors.constructor;

  const install = (GalleryClass) => {
    if (typeof GalleryClass !== 'function') {
      throw new TypeError(
        'Product Hero Gallery lifecycle requires a class constructor.',
      );
    }

    Object.defineProperties(GalleryClass.prototype, descriptors);
  };

  Object.defineProperty(window, MODULE_KEY, {
    configurable: true,
    enumerable: false,
    value: Object.freeze({ install }),
    writable: false,
  });
})();
